import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { principles, siteInfo, testimonials } from "@/mocks/site";

export default function AboutSection() {
  return (
    <section id="about" className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          index="06"
          title="关于曝光档案局"
          subtitle="我们不做判官，只做整理者：把散落各处的翻车现场归档、去身份化，再配上能落地的避雷方法。"
        />

        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <Reveal>
            <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20workspace%20with%20stacked%20paper%20archive%20boxes%2C%20a%20red%20typewriter%20accent%20and%20dried%20flowers%20on%20a%20wooden%20desk%2C%20soft%20natural%20light%2C%20film%20grain%2C%20calm%20documentary%20mood%2C%20no%20people&width=1000&height=1000&seq=expose-about-01&orientation=squarish"
                alt="曝光档案局编辑部桌面：档案盒、红色打字机与干花"
                title="关于曝光档案局 · 把翻车现场整理成档案"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div>
              <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                把翻车现场，整理成一份看得懂的档案
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {siteInfo.name}起步于一个很朴素的念头：深夜刷到的那些情感吐槽，看完只留下情绪，却很少有人把它们整理成可以复用的经验。于是我们开始做这件事——
                把读者投稿的情节重新编排、隐去身份，按「被抓现行、被拆穿瞬间、打脸现场、社死瞬间」分类归档。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                我们不鼓励人肉、不鼓励网暴，也不替任何人下判断。每一辑现场都会标注情节为改编整理，并对涉及隐私的信息做模糊处理。看瓜是看瓜，边界是边界，这两件事我们分得很清楚。
              </p>

              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <p className="flex items-center gap-2 text-xs font-semibold text-foreground-900">
                    <i className="ri-mail-line text-primary-600" />
                    内容合作与纠错
                  </p>
                  <p className="mt-1 text-sm text-primary-700">{siteInfo.email}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <p className="flex items-center gap-2 text-xs font-semibold text-foreground-900">
                    <i className="ri-calendar-check-line text-primary-600" />
                    更新节奏
                  </p>
                  <p className="mt-1 text-sm text-foreground-600">{siteInfo.weeklyUpdate}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
          {principles.map((item, index) => (
            <Reveal key={item.id} delay={index * 80}>
              <div className="h-full rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-800">
                  <i className={item.icon} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-chat-heart-line text-base" />
            </span>
            <h3 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">读者怎么说</h3>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
            {testimonials.map((item, index) => (
              <Reveal key={item.id} delay={index * 60}>
                <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-5">
                  <div className="flex items-center gap-1 text-accent-500">
                    {Array.from({ length: item.rating }).map((_, starIndex) => (
                      <i key={starIndex} className="ri-star-fill text-sm" />
                    ))}
                  </div>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                    {item.content}
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-600 font-heading text-sm font-bold text-background-50">
                      {item.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-foreground-900">{item.name}</span>
                      <span className="block text-[11px] text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}